import { useState } from "react";
import { Link, NavLink } from "react-router";
import {
    Menu,
    MenuButton,
    MenuItem,
    MenuItems,
    MenuSeparator,
} from "@headlessui/react";
import { Bars3Icon } from "@heroicons/react/16/solid";
import {
    UserCircleIcon,
    ArrowRightStartOnRectangleIcon,
    Cog6ToothIcon,
    Squares2X2Icon,
    BriefcaseIcon,
    ClockIcon,
    CalendarIcon,
    ExclamationTriangleIcon,
} from "@heroicons/react/24/outline";
import { useProfileImageUrl } from "@/api/get-profile-picture";
import avatarImg from "@/assets/default_pfp.jpg";
import { useSignOut } from "@/hooks/use-sign-out";
import { Button } from "@/components/ui/button";
import { cn } from "@/utils/cn";
import { ContentNav } from "../nav/";

const navigationMap = {
    true: [
        {
            name: "About",
            to: "/about",
        },
    ],

    false: [
        {
            name: "Find care",
            to: "/clinicians/find",
        },
        { name: "How it works", to: "/how-it-works" },
        {
            name: "For clinicians",
            to: "/for-clinicians",
        },
        {
            name: "About",
            to: "/about",
        },
        {
            name: "Contact",
            to: "/contact",
        },
    ],
};

const profileNavigationMap = {
    clinician: [
        { name: "Profile", to: "/clinician/manage", icon: UserCircleIcon },
        {
            name: "Dashboard",
            to: "/clinician",
            icon: Squares2X2Icon,
            end: true,
        },
        {
            name: "Appointments",
            to: "/clinician/appointments",
            icon: CalendarIcon,
        },
        { name: "Services", to: "/clinician/services", icon: BriefcaseIcon },
        {
            name: "Availability",
            to: "/clinician/availability",
            icon: ClockIcon,
        },

        /*{ name: "Messages", to: "/clinician/", icon: EnvelopeIcon },
        { name: "Settings", to: "/clinician/", icon: Cog6ToothIcon },*/
    ],

    patient: [
        { name: "Profile", to: "/patient/manage", icon: UserCircleIcon },
        {
            name: "Appointments",
            to: "/patient/appointments",
            icon: CalendarIcon,
        },
        /*{ name: "Settings", to: "/patient/", icon: Cog6ToothIcon },*/
    ],
};

export const ContentHeader = ({ user, claims, interestForm, authLayout }) => {
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const navigation = navigationMap[interestForm] || [];
    const profileNavigation = profileNavigationMap[claims] || [];

    const signOut = useSignOut();

    const isClinician = claims === "clinician";
    const { data: profileImageUrl } = useProfileImageUrl(
        user?.uid,
        isClinician,
    );

    return (
        <header className="sticky top-0 z-2 h-[var(--header-height)]">
            <nav
                aria-label="Global"
                className={cn(
                    "w-fill flex max-w-dvw flex-col border-0 bg-white",
                    authLayout ? "pt-4" : "py-4",
                )}
            >
                <div className="px-4">
                    <div className="container mx-auto flex items-center">
                        <div className="me-2 flex lg:hidden">
                            <button
                                type="button"
                                onClick={() => setMobileMenuOpen(true)}
                                className="-ms-2.5 inline-flex items-center justify-center rounded-md p-2.5 text-gray-700"
                            >
                                <span className="sr-only">Open main menu</span>
                                <Bars3Icon
                                    aria-hidden="true"
                                    className="size-6"
                                />
                            </button>
                        </div>
                        <div className="mb-1 flex md:mb-0">
                            <Link
                                to="/"
                                className="-m-1.5 p-1.5 focus-visible:outline-darkgreen-500"
                            >
                                <span className="sr-only">WELNIDO</span>
                                <img
                                    alt="WELNIDO Logo"
                                    src="/logo-darkgreen.svg"
                                    height="80"
                                    width="140"
                                    fetchPriority="high"
                                />
                            </Link>
                        </div>
                        <nav className="ms-6 hidden lg:flex">
                            {navigation.map((item) => (
                                <NavLink
                                    key={item.name}
                                    to={item.to}
                                    end={item.end}
                                    className={({ isActive }) =>
                                        cn(
                                            "-mb-0.5 border-b-2 border-transparent px-3 pt-2 pb-1.5 font-semibold text-darkgreen-500 transition hover:text-darkgreen-600 focus-visible:text-darkgreen-700 focus-visible:shadow-xl focus-visible:outline-0 active:text-darkgreen-700",
                                            isActive && "border-emerald-950",
                                        )
                                    }
                                >
                                    <span>{item.name}</span>
                                </NavLink>
                            ))}
                        </nav>
                        {!interestForm && (
                            <div className="ms-auto items-center gap-4 lg:flex">
                                {user ? (
                                    <div className="relative">
                                        <Menu>
                                            <MenuButton className="flex inline-flex aspect-square items-center justify-center gap-2 rounded-full p-0.5 text-sm/6 font-semibold text-white focus:not-data-focus:outline-none data-focus:outline-2 data-focus:outline-darkgreen-500">
                                                {({ active }) => (
                                                    <img
                                                        src={
                                                            profileImageUrl ||
                                                            avatarImg
                                                        }
                                                        alt="Preview"
                                                        className="size-10 cursor-pointer rounded-full"
                                                        title={
                                                            (active
                                                                ? "Close"
                                                                : "Open") +
                                                            " user navigation menu"
                                                        }
                                                    />
                                                )}
                                            </MenuButton>

                                            <MenuItems
                                                transition
                                                modal={false}
                                                className="absolute top-12 right-0 z-1000 w-52 origin-top-right rounded-xl border border-white/5 bg-beige p-1 text-sm/6 text-darkgreen-500 transition duration-100 ease-out [--anchor-gap:--spacing(1)] focus:outline-none data-closed:scale-95 data-closed:opacity-0"
                                            >
                                                {profileNavigation.length &&
                                                user.emailVerified ? (
                                                    <>
                                                        {profileNavigation.map(
                                                            (item) => (
                                                                <MenuItem
                                                                    key={
                                                                        item.name
                                                                    }
                                                                >
                                                                    <NavLink
                                                                        to={
                                                                            item.to
                                                                        }
                                                                        end={
                                                                            item.end
                                                                        }
                                                                        className="group flex w-full items-center gap-2 rounded-lg px-3 py-1.5 data-focus:bg-black/5"
                                                                    >
                                                                        <item.icon className="size-4" />
                                                                        {
                                                                            item.name
                                                                        }
                                                                    </NavLink>
                                                                </MenuItem>
                                                            ),
                                                        )}
                                                        <MenuSeparator className="my-1 border-t border-gray-200" />
                                                        <MenuItem>
                                                            <NavLink
                                                                to={`${claims}/manage/account`}
                                                                className="group flex w-full items-center gap-2 rounded-lg px-3 py-1.5 data-focus:bg-black/5"
                                                            >
                                                                <Cog6ToothIcon className="size-4" />
                                                                Settings
                                                            </NavLink>
                                                        </MenuItem>
                                                    </>
                                                ) : (
                                                    ""
                                                )}

                                                {!user.emailVerified && (
                                                    <MenuItem>
                                                        <NavLink
                                                            to="/verify-email"
                                                            className="group flex w-full items-center gap-2 rounded-lg px-3 py-1.5 data-focus:bg-black/5"
                                                        >
                                                            <ExclamationTriangleIcon className="size-4" />
                                                            Verify your email
                                                        </NavLink>
                                                    </MenuItem>
                                                )}

                                                <MenuSeparator className="my-1 border-t border-gray-200" />
                                                <MenuItem>
                                                    <button
                                                        className="group flex w-full cursor-pointer items-center gap-2 rounded-lg px-3 py-1.5 data-focus:bg-black/5"
                                                        onClick={signOut}
                                                    >
                                                        <ArrowRightStartOnRectangleIcon className="size-4" />
                                                        Sign out
                                                    </button>
                                                </MenuItem>
                                            </MenuItems>
                                        </Menu>
                                    </div>
                                ) : (
                                    <>
                                        <Link
                                            to="/login"
                                            className="font-semibold"
                                        >
                                            Log in
                                        </Link>
                                        <Button
                                            variant="success"
                                            size="default"
                                            asChild
                                            className="hidden md:flex"
                                        >
                                            <Link to="/register">
                                                List your practice
                                            </Link>
                                        </Button>
                                    </>
                                )}
                            </div>
                        )}
                    </div>
                </div>
                {authLayout && (
                    <div className="mt-2 flex bg-beige/25 px-4 pt-2.5 pb-1.5">
                        <div className="mx-auto min-h-7.75 scrollbar-none overflow-x-auto">
                            {profileNavigation.map((item) => (
                                <NavLink
                                    key={item.name}
                                    to={item.to}
                                    end={item.end}
                                    className={({ isActive }) =>
                                        cn(
                                            "-mb-0.5 border-b-2 border-transparent px-3 pt-2 pb-1.5 text-sm font-semibold text-darkgreen-500 transition hover:text-darkgreen-600 focus-visible:text-darkgreen-700 focus-visible:shadow-xl focus-visible:outline-0 active:text-darkgreen-700",
                                            isActive && "border-emerald-950",
                                        )
                                    }
                                >
                                    <span>{item.name}</span>
                                </NavLink>
                            ))}
                        </div>
                    </div>
                )}
            </nav>
            <ContentNav
                open={mobileMenuOpen}
                onClose={setMobileMenuOpen}
                navigationMap={navigation}
                user={user}
                interestForm={interestForm}
            />
        </header>
    );
};
